import React from 'react';
import Box, { type BoxProps } from '@mui/material/Box';
import Chip, { type ChipProps } from '@mui/material/Chip';

export type EvidenceTone = 'confirmed' | 'emerging' | 'unconfirmed' | 'weakened';
export type StatusTone = 'running' | 'done' | 'go' | 'draft' | 'stop' | 'off';
export type SourceTone = 'stated' | 'suggested' | 'computed' | 'missing';

const EVIDENCE_STYLE: Record<EvidenceTone, { color: string; tint: string }> = {
  confirmed: { color: 'var(--ok, #1b5e20)', tint: 'var(--ok-tint, #e8f5e9)' },
  emerging: { color: 'var(--warn, #7a4a00)', tint: 'var(--warn-tint, #fff4e5)' },
  unconfirmed: { color: 'var(--dim, #616161)', tint: 'var(--dim-tint, transparent)' },
  weakened: { color: 'var(--bad, #8c2f26)', tint: 'var(--bad-tint, #fdecea)' },
};

const STATUS_TO_EVIDENCE: Record<StatusTone, EvidenceTone> = {
  running: 'emerging', done: 'confirmed', go: 'confirmed', draft: 'unconfirmed', stop: 'weakened', off: 'unconfirmed',
};

export function EvidenceChip({ tone, label, ...props }: Omit<ChipProps, 'color'> & { tone: EvidenceTone; label?: React.ReactNode }) {
  const style = EVIDENCE_STYLE[tone];
  return <Chip
    {...props}
    label={label ?? tone.toUpperCase()}
    className={`t-${tone}${props.className ? ` ${props.className}` : ''}`}
    sx={{
      height: 20, fontSize: '0.64rem', fontWeight: 700, letterSpacing: 0.55,
      color: style.color, bgcolor: style.tint, border: '1px solid', borderColor: style.color,
      '& .MuiChip-label': { px: 0.8 },
      ...props.sx,
    }}
  />;
}

export function StatusChip({ status, label, ...props }: Omit<ChipProps, 'color'> & { status: StatusTone; label?: React.ReactNode }) {
  return <EvidenceChip {...props} tone={STATUS_TO_EVIDENCE[status]} label={label ?? status.toUpperCase()} data-status={status} />;
}

export function SourceTag({ source, label, ...props }: Omit<ChipProps, 'color'> & { source: SourceTone; label?: React.ReactNode }) {
  const tone: EvidenceTone = source === 'stated' ? 'confirmed' : source === 'suggested' ? 'emerging' : source === 'missing' ? 'weakened' : 'unconfirmed';
  return <EvidenceChip {...props} tone={tone} label={label ?? source} data-source={source} />;
}

export function EvidenceSourceTag({ label, weak, ...props }: Omit<ChipProps, 'color'> & { label: React.ReactNode; weak?: boolean }) {
  return <Chip {...props} label={label} sx={{
    height: 20, fontSize: '0.68rem', color: weak ? 'var(--ink-3, #8a8781)' : 'var(--ink-2, #6b6862)', bgcolor: 'var(--sunk, #f0eee9)',
    opacity: weak ? 0.55 : 1,
    border: '1px solid var(--line, #e5e3dd)', borderStyle: weak ? 'dashed' : 'solid',
    '& .MuiChip-label': { px: 0.8 }, ...props.sx,
  }} />;
}

export function LevelTag({ level, ...props }: Omit<ChipProps, 'color' | 'label'> & { level: string }) {
  return <Chip {...props} label={level} data-level={level} sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700, color: 'var(--ink, #0F1629)', bgcolor: 'var(--accent-tint, #E7E7FF)', border: '1px solid var(--accent, #4F46E5)', ...props.sx }} />;
}

export function PolicyNote({ children, action, sx, ...props }: React.PropsWithChildren<{ action?: React.ReactNode }> & BoxProps) {
  return <Box {...props} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', color: 'text.secondary', px: 1, py: 0.75, borderRadius: 1.5, bgcolor: 'var(--dim-tint, transparent)', border: '1px solid var(--line-soft, transparent)', ...sx }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    {action}
  </Box>;
}
